import { useState } from "react";
import { passwordSchema } from "@fc/core";
import { useT } from "../../lib/i18n.js";
import { createKeyBundle } from "../../lib/keyring.js";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Input } from "../../components/ui/Input.js";
import { Field } from "../../components/ui/Field.js";
import "./auth.css";
import "../account/RecoverySection.css";

export function ResetPasswordPage() {
  const t = useT();
  // Reading a link never consumes it. Mail scanners and page refreshes cannot reset an account.
  const [token, setToken] = useState(() => window.location.hash.replace(/^#/, ""));
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function reset() {
    setBusy(true); setError(null);
    try {
      const checked = passwordSchema.safeParse(password);
      if (!checked.success) throw new Error(checked.error.issues[0]?.message ?? t("Choose a stronger password."));
      if (password !== confirm) throw new Error(t("The passwords do not match."));
      if (!accepted) return;
      const keys = await createKeyBundle(password);
      await trpcClient.emailRecovery.finish.mutate({ token, newPassword: password, keys, acceptLockedPhotos: true, totpCode: totpCode || undefined });
      setDone(true); setToken(""); setPassword(""); setConfirm(""); setTotpCode("");
      window.history.replaceState(null, "", window.location.pathname);
    } catch (cause) { setError(errorMessage(cause)); }
    finally { setBusy(false); }
  }
  return <div className="fc-auth"><section className="fc-auth__card fc-recovery__card">
    <h1>{t("Reset your password")}</h1>
    {done ? <div role="status">
      <p>{t("Your password has been reset. Sign in with your new password.")}</p>
      <p>{t("Your group memberships remain, but old photos are still locked. Open Your account → Restore group photos to use your recovery file or ask a trusted member from each group.")}</p>
      <p>{t("Two-factor authentication stays enabled. If you just used an authenticator code, wait for the next code before signing in.")}</p>
      <a href="/login">{t("Sign in")}</a>
    </div> : !token ? <><p role="alert">{t("This reset link is missing. Request a new email from the recovery page.")}</p><a href="/recover">{t("Recover your account")}</a></> : <form className="fc-recovery__form" onSubmit={event => { event.preventDefault(); void reset(); }}>
      <p>{t("This resets sign-in without group approval. It creates a new encryption key, so your old group photos will stay locked.")}</p>
      <p>{t("Still have your recovery file or a device that opens your photos? Use those recovery options first to keep access.")}</p>
      <a href="/recover">{t("Use another recovery option")}</a>
      <Field label={t("New password")} required><Input type="password" autoComplete="new-password" value={password} disabled={busy} required onChange={event => setPassword(event.target.value)} /></Field>
      <Field label={t("Confirm new password")} required><Input type="password" autoComplete="new-password" value={confirm} disabled={busy} required onChange={event => setConfirm(event.target.value)} /></Field>
      <Field label={t("Authenticator code, if enabled")}><Input value={totpCode} inputMode="numeric" autoComplete="one-time-code" maxLength={6} disabled={busy} onChange={event => setTotpCode(event.target.value.replace(/\D/g, ""))} /></Field>
      <label><input type="checkbox" checked={accepted} disabled={busy} onChange={event => setAccepted(event.target.checked)} /> {t("I understand that email recovery will not unlock my existing group photos.")}</label>
      <Button type="submit" variant="primary" loading={busy} disabled={!accepted}>{t("Reset password and restore sign-in")}</Button>
      {error && <p role="alert" className="fc-auth__error">{error}</p>}
    </form>}
  </section></div>;
}
